import React, { useState, Dispatch, SetStateAction } from "react";
import { Dialog, Typography, IconButton, Button } from "@material-tailwind/react";
import { Xmark } from "iconoir-react";
import { useDispatch } from "react-redux";

import FileUpload from "./FileUpload";
import { uploadBeforeAfterMediaRequest } from "../redux/vehicles/actions";

interface DialogBeforeAfterUploadProps {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    vehicleId: number;
    sectionId: number;
}

interface FileWithPreview extends File {
    preview?: string;
}

export const DialogBeforeAfterUpload: React.FC<DialogBeforeAfterUploadProps> = ({
    isOpen,
    setIsOpen,
    vehicleId,
}) => {
    const dispatch = useDispatch();
    const [beforeFiles, setBeforeFiles] = useState<FileWithPreview[]>([]);
    const [afterFiles, setAfterFiles] = useState<FileWithPreview[]>([]);

    // Clean up previews when component unmounts
    React.useEffect(() => {
        return () => {
            beforeFiles.forEach(file => {
                if (file.preview) URL.revokeObjectURL(file.preview);
            });
            afterFiles.forEach(file => {
                if (file.preview) URL.revokeObjectURL(file.preview);
            });
        };
    }, [beforeFiles, afterFiles]);

    const handleUpload = () => {
        if (beforeFiles.length || afterFiles.length) {
            // Upload before images
            if (beforeFiles.length && afterFiles.length) {
                const beforeFileList = new DataTransfer();
                beforeFiles.forEach(file => {
                    beforeFileList.items.add(file);
                });
                const afterFileList = new DataTransfer();
                afterFiles.forEach(file => {
                    afterFileList.items.add(file);
                });

                dispatch(
                    uploadBeforeAfterMediaRequest({
                        vehicle_id: vehicleId,
                        before: beforeFileList.files,
                        after: afterFileList.files,
                    })
                );
            }

            setIsOpen(false);
        }
    };

    const handleFileChange = (files: FileList, setFiles: React.Dispatch<React.SetStateAction<FileWithPreview[]>>) => {
        const newFiles: FileWithPreview[] = Array.from(files).map(file => {
            const fileWithPreview = file as FileWithPreview;
            fileWithPreview.preview = URL.createObjectURL(file);
            return fileWithPreview;
        });
        setFiles(newFiles);
    };

    const removeFile = (index: number, files: FileWithPreview[], setFiles: React.Dispatch<React.SetStateAction<FileWithPreview[]>>) => {
        const newFiles = [...files];
        if (newFiles[index].preview) {
            URL.revokeObjectURL(newFiles[index].preview);
        }
        newFiles.splice(index, 1);
        setFiles(newFiles);
    };

    const renderFileList = (files: FileWithPreview[], setFiles: React.Dispatch<React.SetStateAction<FileWithPreview[]>>) => {
        if (!files.length) return null;

        return (
            <div className="flex my-5 flex-col gap-2">
                <div className="grid grid-cols-1 gap-2">
                    {files.map((file, index) => (
                        <div
                            key={index}
                            className="flex flex-col border rounded overflow-hidden"
                        >
                            <div className="relative h-32">
                                <img
                                    src={file.preview}
                                    alt={`Preview ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />
                                <IconButton
                                    variant="ghost"
                                    className="absolute top-1 right-1 bg-white/80 hover:bg-red-500 hover:text-white"
                                    onClick={() => removeFile(index, files, setFiles)}
                                >
                                    <Xmark className="w-4 h-4" />
                                </IconButton>
                            </div>
                            <div className="p-2 bg-gray-50">
                                <Typography className="text-xs text-gray-700 truncate">
                                    {file.name}
                                </Typography>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <Dialog.Overlay>
                <Dialog.Content className="relative p-8">
                    <Typography variant="h4" color="primary" className="font-bold text-lg mb-4">
                        Upload Before & After Photos
                    </Typography>
                    <IconButton
                        variant="ghost"
                        className="absolute top-2 right-2"
                        onClick={() => setIsOpen(false)}
                    >
                        <Xmark className="w-5 h-5" />
                    </IconButton>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {/* Before Images Section */}
                        <div className="flex flex-col">
                            <Typography className="block text-primary font-bold mb-2">
                                Before
                            </Typography>
                            {beforeFiles.length === 0 && (
                                <div className="w-full h-[min(250px,40vh)] flex border-dashed border-2 p-6 items-center justify-center border-gray-400">
                                    <FileUpload
                                        name="before_upload"
                                        isMultiple={false}
                                        accept=".jpg,.png,.jpeg"
                                        title=""
                                        titleClass="hidden"
                                        btnText="Choose Files"
                                        gap="gap-y-3"
                                        desc="Drag & drop before photos here or"
                                        media={null}
                                        onFileChange={(files) =>
                                            handleFileChange(files, setBeforeFiles)
                                        }
                                    />
                                </div>
                            )}
                            {renderFileList(beforeFiles, setBeforeFiles)}
                        </div>

                        {/* After Images Section */}
                        <div className="flex flex-col">
                            <Typography className="block text-primary font-bold mb-2">
                                After
                            </Typography>
                            {afterFiles.length === 0 && (
                                <div className="w-full h-[min(250px,40vh)] flex border-dashed border-2 p-6 items-center justify-center border-gray-400">
                                    <FileUpload
                                        name="after_upload"
                                        isMultiple={false}
                                        accept=".jpg,.png,.jpeg"
                                        title=""
                                        titleClass="hidden"
                                        btnText="Choose Files"
                                        gap="gap-y-3"
                                        desc="Drag & drop after photos here or"
                                        media={null}
                                        onFileChange={(files) => handleFileChange(files, setAfterFiles)}
                                    />
                                </div>
                            )}
                            {renderFileList(afterFiles, setAfterFiles)}
                        </div>
                    </div>

                    <div className="mb-1 flex items-center justify-end gap-2">
                        <Button color="secondary" onClick={() => setIsOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onClick={handleUpload}
                            disabled={!(beforeFiles.length && afterFiles.length)}
                        >
                            Upload
                        </Button>
                    </div>
                </Dialog.Content>
            </Dialog.Overlay>
        </Dialog>
    );
};